import { useEffect, useRef, useState } from "react";

import {
  getRacingLiveSessionBundle,
  type RacingLiveSession,
} from "@/api/racing";

import { appendFlightFrames, buildRaceDataset } from "./build-dataset";
import type { FlightPayload, InfoPayload, RaceDataset, WsEnvelope } from "./race-types";

export interface UseLiveRacingResult {
  dataset: RaceDataset | null;
  latestT: number;
  status: "idle" | "loading" | "live" | "error" | "inactive";
  error: string | null;
  packetsReceived: number;
}

/**
 * Subscribe to a live session — first fetch the buffered bundle (info +
 * flight_history so far) and build the initial dataset, then open a websocket
 * to `/api/ws/racing-live` and append subsequent flight frames (or replace
 * meta on new info payloads). Returns the current dataset + latestT.
 */
export function useLiveRacing(session: RacingLiveSession | null): UseLiveRacingResult {
  const [dataset, setDataset] = useState<RaceDataset | null>(null);
  const [latestT, setLatestT] = useState<number>(0);
  const [status, setStatus] = useState<UseLiveRacingResult["status"]>("idle");
  const [error, setError] = useState<string | null>(null);
  const [packetsReceived, setPacketsReceived] = useState(0);

  const tOriginRef = useRef<number | null>(null);
  const latestInfoRef = useRef<InfoPayload | null>(null);

  useEffect(() => {
    if (!session) {
      setDataset(null);
      setLatestT(0);
      setStatus("idle");
      setError(null);
      setPacketsReceived(0);
      tOriginRef.current = null;
      latestInfoRef.current = null;
      return;
    }

    let cancelled = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;
    let idleGuardTimer: number | null = null;

    setStatus("loading");
    setError(null);
    setPacketsReceived(0);

    async function bootstrap() {
      try {
        const bundle = await getRacingLiveSessionBundle(session!.user_id, session!.key);
        if (cancelled) return;
        const info = (bundle.info ?? bundle.info_history.at(-1)) as InfoPayload | null;
        if (!info) {
          setStatus("error");
          setError("No info payload received yet.");
          return;
        }
        latestInfoRef.current = info;
        const initialDs = buildRaceDataset({
          info,
          flightHistory: bundle.flight_history as FlightPayload[],
          sessionKey: session!.key,
        });
        if (!initialDs) {
          setStatus("error");
          setError("Could not build race dataset — missing home_point.");
          return;
        }
        // Capture t0 from the first frame so subsequent live frames stay on the same clock.
        const firstFrame = (bundle.flight_history as FlightPayload[]).find(
          (f) => (f.vehicles ?? []).some((v) => v.entity_id === 1),
        );
        tOriginRef.current =
          firstFrame && typeof firstFrame.timestamp_ms === "number"
            ? firstFrame.timestamp_ms
            : null;
        setDataset(initialDs);
        setLatestT(
          initialDs.trajectory.length
            ? initialDs.trajectory[initialDs.trajectory.length - 1].t
            : 0,
        );
        setStatus("live");
        openSocket();
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e instanceof Error ? e.message : "Failed to load live session.");
      }
    }

    function scheduleIdleGuard() {
      if (idleGuardTimer !== null) window.clearTimeout(idleGuardTimer);
      idleGuardTimer = window.setTimeout(() => {
        setStatus((prev) => (prev === "live" ? "inactive" : prev));
      }, 8000);
    }

    function openSocket() {
      const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${wsProto}//${window.location.host}/api/ws/racing-live?user_id=${encodeURIComponent(String(session!.user_id))}&key=${encodeURIComponent(session!.key)}`;
      socket = new WebSocket(url);
      socket.addEventListener("message", (ev) => {
        scheduleIdleGuard();
        try {
          const envelope = JSON.parse(ev.data) as WsEnvelope;
          applyEnvelope(envelope);
          setPacketsReceived((n) => n + 1);
        } catch {
          /* ignore */
        }
      });
      socket.addEventListener("close", () => {
        if (cancelled) return;
        setStatus("inactive");
        reconnectTimer = window.setTimeout(() => {
          if (!cancelled) openSocket();
        }, 2000);
      });
      socket.addEventListener("open", () => {
        scheduleIdleGuard();
      });
    }

    function applyEnvelope(envelope: WsEnvelope) {
      if (envelope.type === "info") {
        const info = envelope.payload;
        latestInfoRef.current = info;
        setDataset((prev) => {
          if (!prev) {
            const built = buildRaceDataset({
              info,
              flightHistory: [],
              sessionKey: session!.key,
            });
            return built;
          }
          // Meta-only update (mission/user may change mid-session).
          return {
            ...prev,
            meta: {
              ...prev.meta,
              mission: info.mission_name ?? prev.meta.mission,
              user: info.user_nickname ?? prev.meta.user,
            },
          };
        });
      } else if (envelope.type === "flight") {
        setDataset((prev) => {
          if (!prev) return prev;
          const { dataset: next, tOriginMs } = appendFlightFrames(
            prev,
            [envelope.payload],
            tOriginRef.current,
          );
          tOriginRef.current = tOriginMs;
          if (next.trajectory.length) {
            setLatestT(next.trajectory[next.trajectory.length - 1].t);
          }
          return next;
        });
        setStatus("live");
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      if (idleGuardTimer !== null) window.clearTimeout(idleGuardTimer);
      if (socket) {
        try {
          socket.close();
        } catch {
          /* ignore */
        }
      }
    };
  }, [session?.user_id, session?.key]);

  return { dataset, latestT, status, error, packetsReceived };
}
